import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { printersQuery, type PrinterFleetPage, type PrinterFleetRow } from '../api/inventoree'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'

/**
 * Inventarul de imprimante al flotei - inventoree/printers/index.php plus
 * index-1.js: un rand pe aparat, click deschide masinile de dedesubt.
 */

const PRINTER_CSS = `
    .prf { max-width: 1400px; }
    .prf-lead { color: #6b7682; margin: 0 0 14px; }
    .prf-totals { display: flex; flex-wrap: wrap; gap: 12px; margin: 0 0 18px; }
    .prf-total { background: #fff; border: 1px solid #e6eaee; border-radius: 8px; padding: 10px 16px; min-width: 130px; }
    .prf-total b { display: block; font-size: 22px; line-height: 1.1; color: #34495e; }
    .prf-total span { font-size: 12px; color: #8a94a0; text-transform: uppercase; letter-spacing: .03em; }
    .prf-total.is-warn b { color: #C43626; }
    .prf-table { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1px solid #e6eaee; border-radius: 8px; overflow: hidden; }
    .prf-table th { text-align: left; font-size: 12px; color: #8a94a0; text-transform: uppercase; letter-spacing: .03em; padding: 10px 14px; background: #f7f9fb; border-bottom: 1px solid #e6eaee; }
    .prf-table td { padding: 11px 14px; border-bottom: 1px solid #eef1f4; vertical-align: top; font-size: 13px; color: #34495e; }
    .prf-row { cursor: pointer; }
    .prf-row:hover td { background: #fbfcfd; }
    .prf-row.is-open td { background: #f4f8fb; border-bottom-color: transparent; }
    .prf-model { font-weight: 600; }
    .prf-sub { color: #8a94a0; font-size: 12px; margin-top: 2px; }
    .prf-chip { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; background: #eef1f4; color: #5b6771; margin-right: 4px; }
    .prf-chip.is-ok { background: #e6f5ec; color: #1f7a3f; }
    .prf-chip.is-warn { background: #fff3dc; color: #9e6b00; }
    .prf-chip.is-crit { background: #fbe3e0; color: #C43626; }
    .prf-chip.is-net { background: #e3f1fa; color: #1f6f9e; }
    .prf-chip.is-default { background: #ece9fb; color: #5a48c9; }
    .prf-count { font-weight: 600; }
    .prf-machines { display: none; }
    .prf-machines.is-open { display: table-row; }
    .prf-machines td { background: #f9fbfc; padding: 4px 14px 12px 44px; }
    .prf-mtable { width: 100%; border-collapse: collapse; }
    .prf-mtable td { padding: 6px 8px; border-bottom: 1px dashed #e6eaee; font-size: 13px; background: transparent; }
    .prf-mtable tr:last-child td { border-bottom: 0; }
    .prf-mtable a { color: #34495e; font-weight: 600; }
    .prf-mtable a:hover { color: #36a3d9; }
    .prf-chev { color: #b0b8c1; transition: transform .15s; }
    .prf-row.is-open .prf-chev { transform: rotate(90deg); }
    .prf-empty { background: #fff; border: 1px solid #e6eaee; border-radius: 8px; padding: 22px; color: #6b7682; }
    .prf-empty b { color: #34495e; }
`

/** date('d M Y H:i', strtotime(...)) din printers/index.php. */
export function fleetSeen(raw: string): string {
  if (raw === '') return '—'
  const stamp = raw.includes('T') ? raw : raw.replace(' ', 'T')
  const d = new Date(stamp)
  if (Number.isNaN(d.getTime())) return '—'
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function statusChip(status: string) {
  const s = status.toLowerCase().trim()
  if (s === 'stopped') return <span className="prf-chip is-crit">Stopped</span>
  if (s === 'offline') return <span className="prf-chip is-crit">Offline</span>
  return <span className="prf-chip is-ok">Ready</span>
}

function Fleet({ fleet }: { fleet: PrinterFleetPage }) {
  const [open, setOpen] = useState<Set<string>>(() => new Set())
  const toggle = (key: string) =>
    setOpen((cur) => {
      const next = new Set(cur)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  if (fleet.printers.length === 0) {
    return (
      <div className="prf-empty">
        <b>No printers reported yet.</b> Agents report the print queues configured on each machine at the next full
        inventory (Windows from the spooler, macOS and Linux from CUPS). Print-to-PDF and other virtual printers are
        left out on purpose.
      </div>
    )
  }

  const t = fleet.totals
  return (
    <>
      <div className="prf-totals">
        <div className="prf-total">
          <b>{t.printers}</b>
          <span>Printers</span>
        </div>
        <div className="prf-total">
          <b>{t.network}</b>
          <span>Network</span>
        </div>
        <div className="prf-total">
          <b>{t.local}</b>
          <span>Plugged in</span>
        </div>
        <div className="prf-total">
          <b>{t.machines}</b>
          <span>Machines printing</span>
        </div>
        <div className={`prf-total${t.attention > 0 ? ' is-warn' : ''}`}>
          <b>{t.attention}</b>
          <span>Need attention</span>
        </div>
      </div>
      <table className="prf-table" id="prf-table">
        <thead>
          <tr>
            <th style={{ width: 28 }}></th>
            <th>Printer</th>
            <th>Connection</th>
            <th>Machines</th>
            <th>Default on</th>
            <th>Attention</th>
            <th>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {fleet.printers.map((p, i) => (
            <PrinterRow key={p.key || i} printer={p} index={i} open={open.has(p.key || String(i))} onToggle={() => toggle(p.key || String(i))} />
          ))}
        </tbody>
      </table>
    </>
  )
}

function PrinterRow({ printer: p, index, open, onToggle }: { printer: PrinterFleetRow; index: number; open: boolean; onToggle(): void }) {
  const att = p.attention
  const sub =
    p.name !== p.model
      ? `${p.name} · ${p.location !== '' ? p.location : p.description}`
      : p.location !== ''
        ? p.location
        : p.description

  return (
    <>
      <tr className={`prf-row${open ? ' is-open' : ''}`} data-target={`prf-m-${index}`} onClick={onToggle}>
        <td>
          <i className="fas fa-chevron-right prf-chev" aria-hidden="true"></i>
        </td>
        <td>
          <div className="prf-model">{p.model}</div>
          {sub !== '' && <div className="prf-sub">{sub.replace(/ · $/, '')}</div>}
        </td>
        <td>
          {p.transport === 'network' ? (
            <>
              <span className="prf-chip is-net">Network</span>
              {p.host}
            </>
          ) : (
            <span className="prf-chip">Plugged in</span>
          )}
          {p.shared_from.length > 0 && <div className="prf-sub">Shared from {p.shared_from.join(', ')}</div>}
        </td>
        <td>
          <span className="prf-count">{p.machine_count}</span> machine{p.machine_count === 1 ? '' : 's'}
        </td>
        <td>
          {p.default_on > 0 ? <span className="prf-chip is-default">{p.default_on}</span> : <span className="prf-sub">none</span>}
        </td>
        <td>
          {att.level === 0 ? (
            <span className="prf-chip is-ok">Fine</span>
          ) : (
            <span className={`prf-chip ${att.level >= 2 ? 'is-crit' : 'is-warn'}`}>{att.text.charAt(0).toUpperCase() + att.text.slice(1)}</span>
          )}
        </td>
        <td className="prf-sub">{fleetSeen(p.last_seen)}</td>
      </tr>
      <tr className={`prf-machines${open ? ' is-open' : ''}`} id={`prf-m-${index}`}>
        <td colSpan={7}>
          <table className="prf-mtable">
            <tbody>
              {p.machines.map((m) => (
                <tr key={m.device_id}>
                  <td style={{ width: '34%' }}>
                    <a href={`/app/inventoree/device/${m.device_id}#open=devicePrinters`}>
                      <i className={`fas ${m.is_server ? 'fa-server' : 'fa-desktop'}`} aria-hidden="true"></i> {m.name}
                    </a>
                  </td>
                  <td style={{ width: '26%' }} className="prf-sub">
                    {m.queue !== p.model ? m.queue : ''}
                  </td>
                  <td style={{ width: '14%' }}>{m.default ? <span className="prf-chip is-default">Default</span> : null}</td>
                  <td style={{ width: '12%' }}>{statusChip(m.status)}</td>
                  <td>{m.supplies !== '' ? <span className="prf-chip is-warn">{m.supplies}</span> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </td>
      </tr>
    </>
  )
}

export function PrintersPage() {
  const query = useQuery(printersQuery)
  return (
    <Content>
      <style>{PRINTER_CSS}</style>
      <div className="prf">
        <p className="prf-lead">
          Every printer the agents found configured on your machines, one row per printer. A network printer set up on
          ten workstations is one row with the ten machines under it; click a row to see them. Printers that are
          stopped, offline or short on supplies come first.
        </p>
        {query.isError ? (
          <div className="prf-empty">The printer inventory could not be read just now.</div>
        ) : !query.data ? (
          <Spinner />
        ) : (
          <Fleet fleet={query.data} />
        )}
      </div>
    </Content>
  )
}
